import React, { useMemo } from "react"
import {
  Grid,
  styled,
  Typography,
  Autocomplete,
  FormHelperText,
  TextField,
  MenuItem,
  useTheme
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import StyledTextField from "./AppTextField"

const StyledLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: 8,
  ...theme.typography.body1Regular,
  "& span": {
    color: theme.palette.error.main
  }
}))

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiInputBase-root": {
    ...theme.typography.body1Regular,
    height: 48,
    border: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: 6,
    padding: "0 12px",
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
    "&:hover": {
      borderColor: theme.palette.text.secondary
    },
    "&.Mui-focused": {
      borderColor: theme.palette.text.secondary
    },
    "&.Mui-error": {
      borderColor: theme.palette.error.main
    },
    "&:before": {
      borderBottom: "none !important"
    },
    "&:after": {
      borderBottom: "none !important"
    }
  },
  "& .MuiInputBase-input": {
    padding: "0 !important",
    color: theme.palette.text.primary
  },
  "& .MuiAutocomplete-endAdornment": {
    right: "8px",
    "& .MuiSvgIcon-root": {
      color: theme.palette.text.primary,
      fontSize: "24px"
    }
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: theme.palette.text.primary
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none"
  }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.main} !important`,
  color: theme.palette.text.primary,
  ...theme.typography.body1Regular,
  "&:hover": {
    backgroundColor: `${theme.palette.primary.hover} !important`
  },
  "&.Mui-selected": {
    backgroundColor: `${theme.palette.primary.main} !important`
  }
}))

const AppTextFieldWithSelect = ({
  label,
  name,
  value,
  error,
  touched,
  helperText,
  isRequired = false,
  isDisabled = false,
  isUpperCase = false,
  style = {},
  menuItems = [],
  onChange = () => {},
  onBlur = () => {},
  placeholder,
  select = false,
  ...props
}) => {
  const showError = Boolean(error) && (touched === undefined || touched)
  const theme = useTheme()

  // Normalize options to { label, value }
  const options = useMemo(
    () =>
      (menuItems || []).map((opt) =>
        typeof opt === "string"
          ? { label: opt, value: opt }
          : { label: opt.label ?? opt.value ?? "", value: opt.value ?? opt.label ?? "" }
      ),
    [menuItems]
  )

  // Selected option object based on stored value
  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find((opt) => opt.value === value) || { label: value, value }
  }, [options, value])

  const handleChange = (_, newVal) => {
    const nextValue =
      typeof newVal === "string" ? newVal : newVal?.value !== undefined ? newVal.value : ""
    onChange({
      target: { name, value: nextValue }
    })
  }

  const handleInputChange = (_, newInputValue) => {
    onChange({
      target: { name, value: newInputValue || "" }
    })
  }

  if (!select) {
    return (
      <StyledTextField
        label={label}
        name={name}
        value={value}
        error={error}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isUpperCase={isUpperCase}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        style={style}
        {...props}
      />
    )
  }

  return (
    <Grid container style={style}>
      {label && (
        <StyledLabel>
          {!isUpperCase ? label.toUpperCase() : label}
          {isRequired && <span>*</span>}
        </StyledLabel>
      )}

      <Grid item xs={12}>
        <StyledAutocomplete
          freeSolo
          value={selectedOption}
          inputValue={selectedOption?.label || value || ""}
          onChange={handleChange}
          onInputChange={handleInputChange}
          disabled={isDisabled}
          options={options}
          getOptionLabel={(option) => (typeof option === "string" ? option : option?.label || "")}
          isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
          popupIcon={<KeyboardArrowDownIcon />}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              onBlur={onBlur}
              error={showError}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true
              }}
            />
          )}
          renderOption={(props, option) => {
            const opt = typeof option === "string" ? { label: option, value: option } : option
            return (
              <StyledMenuItem {...props} key={opt.value || opt.label} value={opt.value}>
                {opt.label}
              </StyledMenuItem>
            )
          }}
          PaperComponent={(props) => (
            <div
              {...props}
              style={{
                marginTop: 8,
                maxHeight: 300,
                backgroundColor: `${theme.palette.primary.main} !important`,
                boxShadow: "4px 4px 14px 0px #161616"
              }}>
              <div
                style={{
                  backgroundColor: "#1A1A1A",
                  borderRadius: 6
                }}>
                {props.children}
              </div>
            </div>
          )}
          {...props}
        />
      </Grid>

      {showError && <FormHelperText error>{error || helperText}</FormHelperText>}
    </Grid>
  )
}

export default AppTextFieldWithSelect
